import Form from "components/ui/form/Form";
import { Button } from "components/ui/button/Button";
import { Controller, useForm, Control, UseFormRegister, ControllerRenderProps } from "react-hook-form";
import "./multiple-selection.scss";
import "./multiple-choice.scss";
import {
  SubmittedInputInfoRequest,
  useSetApplicationStepAnswerMutation,
  InputResponse,
  useUpdateStepAnswerCyberSecurityAuditLogMutation,
} from "pages/application/applicationService";
import { useSelector } from "react-redux";
import { getStepData } from "pages/application/question/questionSlice";
import { FormInput } from "components/ui/form/FormInput";
import Checkbox from "components/ui/checkbox/Checkbox";
import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { getCurrentUserApplicationId } from "features/authSlice";
import { SkipButton } from "pages/application/question/Question";
import { QuestionActions } from "pages/application/templates/shared/QuestionActions";
import { QuestionNavigation } from "pages/application/templates/shared/QuestionNavigation";
import { MAX_RACE_SELECTIONS } from "utilities/Constant";
import { trackFSStepCompleted } from "utilities/full-story";

type IFormData = Record<string, boolean | string>;

interface ChildInputsParams {
  control: Control<IFormData>;
  register: UseFormRegister<IFormData>;
  selectedInputs: string[];
  isReadonly: boolean;
  handleInputCheck: (name: string, parentName?: string) => void;
  handleInputUncheck: (name: string, parentName?: string) => void;
}

const createChildControllerRender = (
  label: string,
  name: string,
  parentName: string,
  params: ChildInputsParams
) => {
  const createChildControllerRender = ({ field }: { field: ControllerRenderProps<IFormData> }) => (
    <Checkbox
      label={label}
      onChange={(value) => {
        field.onChange(value);
        if (value) params.handleInputCheck(name, parentName);
        else params.handleInputUncheck(name, parentName);
      }}
      checkboxState={params.selectedInputs.includes(name)}
      disabled={params.isReadonly}
    />
  );
  createChildControllerRender.displayName = "ChildControllerRender";
  return createChildControllerRender;
};

const renderGrandChildInputs = (
  grandChildInputs: InputResponse[],
  parentName: string,
  { register, selectedInputs, isReadonly }: ChildInputsParams
) => {
  const isChecked = selectedInputs.includes(parentName);
  return (
    <div className="grandchild-inputs">
      {grandChildInputs.map(({ inputId, name, placeholder, description, dataType }) => (
        <div className="grandchild-inputs__input-container" key={inputId}>
          <FormInput
            formHandle={register(name)}
            inputProps={{
              name,
              placeholder: placeholder,
              subText: description,
              type: dataType,
              isDisabled: isReadonly || !isChecked,
            }}
          />
        </div>
      ))}
    </div>
  );
};

const renderChildInputs = (
  childInputs: InputResponse[],
  parentName: string,
  params: ChildInputsParams
) => {
  const { control, register, selectedInputs, isReadonly } = params;
  const isChecked = selectedInputs.includes(parentName);
  return (
    <div className="child-inputs">
      {childInputs.map(({ inputId, label, name, dataType, ...rest }) => {
        if (dataType === "multiSelect") {
          return (
            <div key={inputId} className="child-inputs__checkbox-container">
              <Controller
                name={name}
                control={control}
                render={createChildControllerRender(label, name, parentName, params)}
              />
              {renderGrandChildInputs(rest.childInputs, name, params)}
            </div>
          );
        } else {
          return (
            <div key={inputId} className="child-inputs__input-container">
              <FormInput
                formHandle={register(name)}
                inputProps={{
                  name,
                  placeholder: rest.placeholder,
                  subText: rest.description,
                  type: dataType,
                  isDisabled: isReadonly || !isChecked,
                }}
              />
            </div>
          );
        }
      })}
    </div>
  );
};

const MultipleSelection = () => {
  const stepData = useSelector(getStepData);
  const applicationId = useSelector(getCurrentUserApplicationId);
  const [updateCyberSecurityAuditLog] = useUpdateStepAnswerCyberSecurityAuditLogMutation();
  const [updateApplication] = useSetApplicationStepAnswerMutation({
    fixedCacheKey: "updateApplication",
  });
  const { handleSubmit, register, control, setValue } = useForm<IFormData>();
  const { inputs, submitAction, skipAction, stepCode, label, isReadOnly } = {
    inputs: stepData.inputs,
    skipAction: stepData.skipAction,
    submitAction: stepData.nextStepActions.find(({ actionType }) => actionType === "next")!,
    stepCode: stepData.stepCodeText,
    label: stepData.label,
    isReadOnly: stepData.isReadOnly ?? false,
  };

  const [selectedInputs, setSelectedInputs] = useState<string[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [isReadonly, setIsReadonly] = useState<boolean>(false);

  useEffect(() => {
    setIsReadonly(isReadOnly);
  }, []);

  const setInitialValue = (input: InputResponse) => {
    if (input.dataType === "multiSelect" && input.initialValue === "selected") {
      setSelectedInputs((prevSelected) => [...prevSelected, input.name]);
      setValue(input.name, true);
    } else if (input.dataType === "rawString" && input.initialValue !== "") {
      setValue(input.name, input.initialValue);
    }

    input.childInputs.forEach((childInput) => {
      setInitialValue(childInput);
    });
  };

  useEffect(() => {
    inputs.forEach((input) => {
      setInitialValue(input);
    });
  }, []);

  useEffect(() => {
    setSelectedInputs((prevSelected) => {
      const uniqueSelected = Array.from(new Set(prevSelected));
      if (uniqueSelected.length !== prevSelected.length) {
        return uniqueSelected;
      }
      return prevSelected;
    });
  }, [selectedInputs]);
  const I_DO_NOT_WISH_TO_ANSWER = inputs.find((input) => input.label.startsWith("I do not wish to"))?.name ?? "";

  const isRaceStep = stepCode === "J4";
  const isEthnicityStep = stepCode === "J3";
  const isGenderStep = stepCode === "J8";
  const isMandatoryCheckboxStep = isRaceStep || isEthnicityStep || isGenderStep;

  const clearIDoNotWishToAnswer = () => {
    setSelectedInputs((prevSelected) => prevSelected.filter((selectedName) => selectedName !== I_DO_NOT_WISH_TO_ANSWER));
    setValue(I_DO_NOT_WISH_TO_ANSWER, false);
  };

  const clearChildInputs = ({ childInputs }: InputResponse) => {
    childInputs.forEach((childInput) => {
      if (childInput.dataType === "multiSelect") {
        const isNotThisInput = (name: string) => name !== childInput.name;
        setSelectedInputs((prevSelected) => prevSelected.filter(isNotThisInput));
        setValue(childInput.name, false);
        clearChildInputs(childInput);
      } else {
        setValue(childInput.name, "");
      }
    });
  };

  const handleInputCheck = (name: string, parentName?: string) => {
    if (isRaceStep && selectedInputs.length >= MAX_RACE_SELECTIONS) return;
    if (name === I_DO_NOT_WISH_TO_ANSWER) {
      setSelectedInputs([name]);
      inputs.forEach((input: InputResponse) => {
        if (input.name !== I_DO_NOT_WISH_TO_ANSWER) {
          setValue(input.name, false);
          input.childInputs.length != 0 && clearChildInputs(input);
        }
      });
    } else {
      const isChildInput = parentName && !selectedInputs.includes(parentName);
      if (isChildInput) {
        setSelectedInputs((prevSelected) => [...prevSelected, name, parentName]);
        setValue(parentName, true);
      } else {
        setSelectedInputs((prevSelected) => [...prevSelected, name]);
      }
      selectedInputs.includes(I_DO_NOT_WISH_TO_ANSWER) && clearIDoNotWishToAnswer();
    }
  };

  const handleInputUncheck = (name: string, parentName?: string) => {
    setSelectedInputs((prevSelected) => prevSelected.filter((selectedName) => selectedName !== name));
    const currentInput = parentName
      ? inputs.find((input) => input.name === parentName)!.childInputs.find((childInput) => childInput.name === name)!
      : inputs.find((input) => input.name === name)!;
    currentInput.childInputs.length != 0 && clearChildInputs(currentInput);
  };

  const onSubmit = async (formData: IFormData) => {
    if (selectedInputs.length === 0 && isMandatoryCheckboxStep) {
      setIsError(true);
      return;
    }
    const submittedInputs: SubmittedInputInfoRequest[] = [];
    const processInput = ({ inputId, name, dataType, childInputs }: InputResponse) => {
      let submittedValue = "";
      if (formData[name]) {
        if (dataType === "multiSelect") {
          if (selectedInputs.includes(name)) {
            submittedValue = "selected";
          }
        } else {
          submittedValue = String(formData[name]);
        }
      }
      submittedInputs.push({
        inputId,
        name,
        submittedValue,
      });

      if (childInputs.length > 0) {
        childInputs.forEach((childInput) => {
          processInput(childInput);
        });
      }
    };

    inputs.forEach((input) => {
      processInput(input);
    });

    await updateApplication({
      id: applicationId,
      stepActionId: submitAction.stepActionId,
      answeredInputs: submittedInputs,
    });

    trackFSStepCompleted(stepCode, label, false);
    void updateCyberSecurityAuditLog(applicationId);
  };

  const handleSkip = async () => {
    const submittedInputs: SubmittedInputInfoRequest[] = [];

    const resetInput = ({ inputId, name, childInputs }: InputResponse) => {
      const submittedValue = "";
      submittedInputs.push({
        inputId,
        name,
        submittedValue,
      });

      if (childInputs.length > 0) {
        childInputs.forEach((childInput) => {
          resetInput(childInput);
        });
      }
    };

    inputs.forEach((input: InputResponse) => {
      setValue(input.name, false);
      input.childInputs.length != 0 && clearChildInputs(input);
      resetInput(input);
    });

    await updateApplication({
      id: applicationId,
      stepActionId: skipAction?.stepActionId,
      answeredInputs: submittedInputs,
    });
    trackFSStepCompleted(stepCode, label, true);
    void updateCyberSecurityAuditLog(applicationId);
  };

  return (
    <Form formClassName="multiple-choice col">
      <div className="multiple-select">
        {isError && (
          <div className="multiple-select__error">
            <FontAwesomeIcon icon={solid("exclamation-circle")} />
            <p>You must make a selection.</p>
          </div>
        )}
        {isReadonly && (
          <div className="multiple-select__readonly-warning">
            <p>To make changes please reach out to your Loan Officer.</p>
          </div>
        )}
        {inputs.map(({ inputId, name, label, childInputs }) => {
          const isChecked = selectedInputs.includes(name);
          return (
            <div className="multiple-select__input-container" key={inputId}>
              <Controller
                name={name}
                control={control}
                render={({ field }) => {
                  return (
                    <Checkbox
                      label={label}
                      onChange={(value) => {
                        field.onChange(value);
                        if (value) handleInputCheck(name);
                        else handleInputUncheck(name);
                      }}
                      checkboxState={isChecked}
                      disabled={isReadonly || (name === I_DO_NOT_WISH_TO_ANSWER && selectedInputs.length > 0 && !isChecked)}
                    />
                  );
                }}
              />
              {renderChildInputs(childInputs, name, { control, register, selectedInputs, isReadonly, handleInputCheck, handleInputUncheck })}
            </div>
          );
        })}

        <QuestionActions skip={skipAction && <SkipButton skipAction={skipAction} skipActionHandler={handleSkip} />} />
        <QuestionNavigation>
          <Button title={submitAction.label} className="question-navigation__continue" onClick={handleSubmit(onSubmit)} />
        </QuestionNavigation>
      </div>
    </Form>
  );
};

export default MultipleSelection;
